import OrderOptimizerDetailsOtherResponse, {
  OrderOptimizerDetailsOtherResponseItemDriver,
  OrderOptimizerDetailsOtherResponseItemOptimizedStats,
  OrderOptimizerDetailsOtherResponseItemOrders,
  OrderOptimizerDetailsOtherResponseItemOrdersByDriverAba,
  OrderOptimizerDetailsOtherResponseItemOrdersByDriverSabs,
  OrderOptimizerDetailsOtherResponseItemStatus,
  OrderOptimizerDetailsOtherResponseItemSteps,
  OrderOptimizerDetailsOtherResponseItemStepsEndToHome,
  OrderOptimizerDetailsOtherResponseItemStepsHomeToStart,
  OrderOptimizerDetailsOtherResponseItemStepsOrders,
  OrderOptimizerDetailsOtherResponseItemStepsOrdersPoints,
  OrderOptimizerDetailsOtherResponseItemUnoptimizedStats,
} from "../../../../api/raily/order-optimizer/details/other/order-optimizer-details-other.response";
import OrderOptimizerDetailsOther, {
  OrderOptimizerDetailsOtherDriver,
  OrderOptimizerDetailsOtherOptimizedStats,
  OrderOptimizerDetailsOtherOrders,
  OrderOptimizerDetailsOtherOrdersByDriverAba,
  OrderOptimizerDetailsOtherOrdersByDriverSabs,
  OrderOptimizerDetailsOtherStatus,
  OrderOptimizerDetailsOtherSteps,
  OrderOptimizerDetailsOtherStepsEndToHome,
  OrderOptimizerDetailsOtherStepsHomeToStart,
  OrderOptimizerDetailsOtherStepsOrders,
  OrderOptimizerDetailsOtherStepsOrdersPoints,
  OrderOptimizerDetailsOtherUnoptimizedStats,
} from "./order-optimizer-details-other";

const createStepsOrderPoint = (
  stepsOrderPoint: OrderOptimizerDetailsOtherResponseItemStepsOrdersPoints,
): OrderOptimizerDetailsOtherStepsOrdersPoints => {
  const point: OrderOptimizerDetailsOtherStepsOrdersPoints = {
    displayName: stepsOrderPoint.displayName,
    distance: stepsOrderPoint.distance,
    driverDistance: stepsOrderPoint.driverDistance ?? 0,
    driverDrivingTime: stepsOrderPoint.driverDrivingTime ?? 0,
    driverHaltingTime: stepsOrderPoint.driverHaltingTime ?? 0,
    driverTime: stepsOrderPoint.driverTime,
    haltingTime: stepsOrderPoint.haltingTime,
    lat: stepsOrderPoint.lat,
    lon: stepsOrderPoint.lon,
    time: stepsOrderPoint.time,
  };

  return point;
};

const createStepsOrderPoints = (
  stepsOrderPoints: OrderOptimizerDetailsOtherResponseItemStepsOrdersPoints[],
): OrderOptimizerDetailsOtherStepsOrdersPoints[] => {
  return stepsOrderPoints.map(createStepsOrderPoint);
};

const createStepsOrder = (
  stepsOrder: OrderOptimizerDetailsOtherResponseItemStepsOrders,
): OrderOptimizerDetailsOtherStepsOrders => {
  const order = {
    distance: stepsOrder.distance,
    haltingTime: stepsOrder.haltingTime,
    points: createStepsOrderPoints(stepsOrder.points),
    totalTime: stepsOrder.totalTime,
  };

  return order;
};

const createStepsOrders = (
  stepsOrders: OrderOptimizerDetailsOtherResponseItemStepsOrders[],
): OrderOptimizerDetailsOtherStepsOrders[] => {
  return stepsOrders.map(createStepsOrder);
};

const createStepsHomeToStart = (
  stepsHomeToStart: OrderOptimizerDetailsOtherResponseItemStepsHomeToStart | null,
): OrderOptimizerDetailsOtherStepsHomeToStart | null => {
  if (!stepsHomeToStart) return null;
  const startStep = {
    displayName: stepsHomeToStart.displayName,
    driverTime: stepsHomeToStart.driverTime,
  };
  return startStep;
};

const createStepsEndToHome = (
  stepsEndToHome: OrderOptimizerDetailsOtherResponseItemStepsEndToHome | null,
): OrderOptimizerDetailsOtherStepsEndToHome | null => {
  if (!stepsEndToHome) return null;
  const endStep = {
    displayName: stepsEndToHome.displayName,
    driverDistance: stepsEndToHome.driverDistance,
    driverDrivingTime: stepsEndToHome.driverDrivingTime,
    driverTime: stepsEndToHome.driverTime,
  };
  return endStep;
};

const createSteps = (
  steps: OrderOptimizerDetailsOtherResponseItemSteps,
): OrderOptimizerDetailsOtherSteps => {
  const detailsSteps = {
    endToHome: createStepsEndToHome(steps.endToHome),
    homeToStart: createStepsHomeToStart(steps.homeToStart),
    orders: createStepsOrders(steps.orders),
  };
  return detailsSteps;
};

const createOrdersBySabs = (
  ordersBySabs: OrderOptimizerDetailsOtherResponseItemOrdersByDriverSabs | null,
): OrderOptimizerDetailsOtherOrdersByDriverSabs | null => {
  if (!ordersBySabs) return null;
  const orders = {
    distance: ordersBySabs.distance,
    haltingTime: ordersBySabs.haltingTime,
    totalTime: ordersBySabs.totalTime,
  };
  return orders;
};

const createOrdersByAba = (
  ordersByABa: OrderOptimizerDetailsOtherResponseItemOrdersByDriverAba | null,
): OrderOptimizerDetailsOtherOrdersByDriverAba | null => {
  if (!ordersByABa) return null;
  const orders = {
    distance: ordersByABa.distance,
    haltingTime: ordersByABa.haltingTime,
    totalTime: ordersByABa.totalTime,
  };
  return orders;
};

const createOrder = (
  order: OrderOptimizerDetailsOtherResponseItemOrders,
): OrderOptimizerDetailsOtherOrders => {
  const detailsOrder = {
    billingModel: order.billingModel,
    cargoCompany: order.cargoCompany,
    distance: order.distance,
    driverName: order.driverName,
    fleetPartner: order.fleetPartner,
    haltingTime: order.haltingTime,
    humanId: order.humanId,
    planEntryHumanId: order.planEntryHumanId,
    startTime: order.startTime,
    taxiCorporation: order.taxiCorporation,
    totalTime: order.totalTime,
  };
  return detailsOrder;
};

const createOrders = (
  orders: OrderOptimizerDetailsOtherResponseItemOrders[],
): OrderOptimizerDetailsOtherOrders[] => {
  return orders.map(createOrder);
};

const createOptimizedStats = (
  optimizedStats: OrderOptimizerDetailsOtherResponseItemOptimizedStats,
): OrderOptimizerDetailsOtherOptimizedStats => {
  const stats = {
    contractRate: optimizedStats.contractRate,
    distance: optimizedStats.distance,
    haltingTime: optimizedStats.haltingTime,
    margin: optimizedStats.margin,
    totalTime: optimizedStats.totalTime,
  };
  return stats;
};

const createUnoptimizedStats = (
  optimizedStats: OrderOptimizerDetailsOtherResponseItemUnoptimizedStats,
): OrderOptimizerDetailsOtherUnoptimizedStats => {
  const stats = {
    contractRate: optimizedStats.contractRate,
    distance: optimizedStats.distance,
    haltingTime: optimizedStats.haltingTime,
    margin: optimizedStats.margin,
    totalTime: optimizedStats.totalTime,
  };
  return stats;
};

const createDriver = (
  driver: OrderOptimizerDetailsOtherResponseItemDriver | null,
): OrderOptimizerDetailsOtherDriver | null => {
  if (!driver) return null;
  const detailsDriver = {
    contractRate: driver.contractRate,
    displayName: driver.displayName,
    driverId: driver.driverId,
    startLat: driver.startLat,
    startLon: driver.startLon,
    taxiCorporation: driver.taxiCorporation,
  };
  return detailsDriver;
};

const createStatus = (
  status: OrderOptimizerDetailsOtherResponseItemStatus,
): OrderOptimizerDetailsOtherStatus => {
  switch (status) {
    case OrderOptimizerDetailsOtherResponseItemStatus.INTERESTING:
      return OrderOptimizerDetailsOtherStatus.INTERESTING;
    case OrderOptimizerDetailsOtherResponseItemStatus.PENDING:
      return OrderOptimizerDetailsOtherStatus.PENDING;
    case OrderOptimizerDetailsOtherResponseItemStatus.REJECTED:
      return OrderOptimizerDetailsOtherStatus.REJECTED;
  }
};

const create = (
  response: OrderOptimizerDetailsOtherResponse,
): OrderOptimizerDetailsOther => {
  return {
    driver: createDriver(response.data.driver),
    optimizedStats: createOptimizedStats(response.data.optimizedStats),
    orders: createOrders(response.data.orders),
    ordersByDriverAba: createOrdersByAba(response.data.ordersByDriverAba),
    ordersByDriverSabs: createOrdersBySabs(response.data.ordersByDriverSabs),
    reviewedAt: response.data.reviewedAt,
    reviewedBy: response.data.reviewedBy,
    saAndBsDistance: response.data.saAndBsDistance ?? 0,
    steps: createSteps(response.data.steps),
    unoptimizedStats: createUnoptimizedStats(response.data.unoptimizedStats),
    score: response.data.score,
    totalCargoIncome: response.data.totalCargoIncome,
    totalDriverHaltingCost: response.data.totalDriverHaltingCost ?? 0,
    status: createStatus(response.data.status),
  };
};

const orderOptimizerDetailsOtherFactory = {
  create,
};

export default orderOptimizerDetailsOtherFactory;
